import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {CommonModule, DatePipe} from '@angular/common';
import {TranslateModule} from '@ngx-translate/core';
import {ProgressBarModule} from 'primeng/progressbar';
import {Loading} from '@components/ui/loading/loading';
import {TabsDynamic, TabItem} from '@components/ui/tabs/tabs-dynamic';
import {ServerTranslatePipe} from '@app/utils/pipes/server-translate-pipe';
import {FieldDetailsAnomalyRowComponent} from '../field-details-anomaly-row/field-details-anomaly-row.component';
import type {FieldDetailsRecommendationView} from '../../managers/field-details-recommendations.util';

@Component({
  selector: 'app-field-details-indices-bottom-grid',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    ProgressBarModule,
    Loading,
    TabsDynamic,
    ServerTranslatePipe,
    FieldDetailsAnomalyRowComponent,
  ],
  templateUrl: './field-details-indices-bottom-grid.component.html',
  styleUrls: ['./field-details-indices-bottom-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DatePipe],
})
export class FieldDetailsIndicesBottomGridComponent implements AfterViewInit, OnChanges {
  private readonly datePipe = inject(DatePipe);
  private readonly cdr = inject(ChangeDetectorRef);

  @ViewChild('recoTabTpl', { static: false }) recoTabTpl!: TemplateRef<unknown>;
  @ViewChild('agroTabTpl', { static: false }) agroTabTpl!: TemplateRef<unknown>;
  @ViewChild('recoListScroll') recoListScroll?: ElementRef<HTMLDivElement>;

  detailTabs: TabItem[] = [];

  @Input() detailActiveTab = 'reco';
  @Input() anomaliesLoading = false;
  @Input() anomaliesForSelectedDay: any[] = [];
  @Input() recommendations: FieldDetailsRecommendationView[] = [];
  @Input() focusedRecommendationCode: number | null = null;
  @Input() agroMeasures: any[] = [];
  @Input() plantingBoundaryDate: Date | null = null;

  @Input() fieldPhotosLoading = false;
  @Input() fieldPhotosProgress = 0;
  @Input() photoStrip: { src: string; name: string }[] = [];
  @Input() photosTotal = 0;
  @Input() dataUpdatedDisplay: Date | null = null;

  @Output() showAnomalyOnMap = new EventEmitter<any>();
  @Output() detailActiveTabChange = new EventEmitter<string>();
  @Output() openPhotoSlider = new EventEmitter<number>();
  @Output() openPhotoSliderFromMore = new EventEmitter<void>();

  ngAfterViewInit(): void {
    this.detailTabs = [
      { id: 'reco', label: 'FIELD_DETAILS_V2.TAB_RECOMMENDATIONS', template: this.recoTabTpl },
      { id: 'agro', label: 'FIELD_DETAILS_V2.TAB_AGRO_CALENDAR', template: this.agroTabTpl },
    ];
    this.cdr.markForCheck();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['focusedRecommendationCode']?.currentValue != null) {
      queueMicrotask(() => this.scrollToFocusedRecommendation());
    }
  }

  onDetailTabChange(tabId: string): void {
    this.detailActiveTabChange.emit(tabId);
  }

  private scrollToFocusedRecommendation(): void {
    const code = this.focusedRecommendationCode;
    if (code == null) {
      return;
    }
    const host = this.recoListScroll?.nativeElement;
    if (!host) {
      return;
    }
    const target = host.querySelector(`[data-reco-code="${code}"]`);
    target?.scrollIntoView({behavior: 'smooth', block: 'nearest'});
  }

  /** No scheduled window when both day offsets are 0 → date is hidden. */
  hasAgroWindow(m: any): boolean {
    return Number(m?.dayStart ?? 0) !== 0 || Number(m?.dayEnd ?? 0) !== 0;
  }

  agroMeasureWindow(m: any): string {
    const plant = this.plantingBoundaryDate;
    if (!plant || !this.hasAgroWindow(m)) {
      return '';
    }
    const a = this.addDays(plant, m.dayStart ?? 0);
    const b = this.addDays(plant, m.dayEnd ?? m.dayStart ?? 0);
    const f = (d: Date) => this.datePipe.transform(d, 'dd.MM.yyyy');
    return `${f(a)} – ${f(b)}`;
  }

  private addDays(base: Date, days: number): Date {
    const d = new Date(base);
    d.setDate(d.getDate() + Number(days || 0));
    return d;
  }
}
